import React from "react";
import { Plus, Settings, Trash2 } from "lucide-react";

const TYPE_BADGE = {
  PAGO_MOVIL:   { label: "Pago Móvil",    cls: "bg-cyan-500/20 border-cyan-500/40 text-cyan-300" },
  TRANSFERENCIA:{ label: "Transferencia", cls: "bg-purple-500/20 border-purple-500/40 text-purple-300" },
  USD_PLATFORM: { label: "Pago en $",     cls: "bg-green-500/20 border-green-500/40 text-green-300" },
};

/**
 * AdminPagosTab — CRUD grid for the payment methods that appear in the
 * customer checkout (Pago Móvil, Transferencia, USD platforms like
 * Zinli/Zelle/Binance).
 *
 * The `Agregar/Editar` modal lives in the parent App (it's shared with the
 * seed-time bootstrap). This component only lists the methods and delegates
 * open/edit/toggle/delete to callbacks.
 */
export function AdminPagosTab({
  paymentMethods,
  onOpenAdd,
  onEdit,
  onToggleActive,
  onDelete,
}) {
  return (
    <div className="space-y-6" data-testid="admin-payments-view">
      <div className="bg-ag7-surface border border-[var(--ag7-border-soft)] rounded-2xl p-6 flex flex-wrap gap-3 justify-between items-center">
        <div>
          <h3 className="text-xl font-heading font-extrabold tracking-tight text-white mb-1">Cuentas y Métodos de Pago</h3>
          <p className="text-xs text-zinc-400">Administra Pago Móvil, Transferencia y Pago en $. Sólo los métodos activos se muestran al cliente.</p>
        </div>
        <button
          onClick={onOpenAdd}
          data-testid="admin-open-add-bank-modal"
          className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Agregar Nueva Cuenta/Método
        </button>
      </div>

      {paymentMethods.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 text-sm" data-testid="admin-payments-empty">
          No hay métodos de pago registrados. Crea el primero.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-payments-grid">
          {paymentMethods?.map((pm) => (
            <PaymentMethodCard
              key={pm.id}
              pm={pm}
              onEdit={onEdit}
              onToggleActive={onToggleActive}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PaymentMethodCard({ pm, onEdit, onToggleActive, onDelete }) {
  const isPagoMovil = pm.type === "PAGO_MOVIL";
  const isTransfer  = pm.type === "TRANSFERENCIA";
  const isUsd       = pm.type === "USD_PLATFORM";
  const badge = TYPE_BADGE[pm.type] || TYPE_BADGE.PAGO_MOVIL;

  return (
    <div
      data-testid={`admin-pm-card-${pm.id}`}
      className={`rounded-2xl p-5 border font-mono text-xs relative ${pm.active ? "bg-ag7-surface border-[var(--ag7-border-soft)]" : "bg-ag7-bg/60 border-zinc-800/50 opacity-70"}`}
    >
      <div className="flex items-start justify-between gap-2 border-b border-[var(--ag7-border)] pb-3 mb-3">
        <div className="min-w-0 flex-1">
          <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-widest ${badge.cls}`}>
            {badge.label}
          </span>
          <h4 className="font-bold text-white text-sm mt-2 truncate">
            {isUsd ? pm.platformName : pm.bankName || "—"}
            {isPagoMovil && pm.bankCode && <span className="text-zinc-500 font-normal"> ({pm.bankCode})</span>}
          </h4>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <button
            onClick={() => onToggleActive(pm)}
            data-testid={`admin-pm-toggle-active-${pm.id}`}
            title={pm.active ? "Desactivar" : "Activar"}
            className={`px-2 py-1 rounded-lg text-[10px] font-bold border ${pm.active ? "bg-green-500/20 border-green-500/40 text-green-300 hover:bg-green-500/30" : "bg-ag7-surface-2 border-[var(--ag7-border-soft)] text-zinc-400 hover:text-white"}`}
          >
            {pm.active ? "ACTIVO" : "INACTIVO"}
          </button>
          <div className="flex gap-1">
            <button
              onClick={() => onEdit(pm)}
              data-testid={`admin-pm-edit-${pm.id}`}
              className="p-1.5 rounded-lg bg-ag7-surface-2 hover:bg-zinc-700 text-zinc-300 border border-[var(--ag7-border-soft)]"
              title="Editar"
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete(pm.id)}
              data-testid={`admin-pm-delete-${pm.id}`}
              className="p-1.5 rounded-lg bg-red-950/60 hover:bg-red-900 text-red-300 border border-red-900/50"
              title="Eliminar"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {isPagoMovil && (
        <div className="space-y-1.5">
          <p className="text-zinc-400">Titular: <strong className="text-white">{pm.accountHolder || "-"}</strong></p>
          <p className="text-zinc-400">Cédula/RIF: <strong className="text-white">{pm.documentId || "-"}</strong></p>
          <p className="text-zinc-400">Teléfono: <strong className="text-white">{pm.phone || "-"}</strong></p>
        </div>
      )}
      {isTransfer && (
        <div className="space-y-1.5">
          <p className="text-zinc-400">Titular: <strong className="text-white">{pm.accountHolder || "-"}</strong></p>
          <p className="text-zinc-400">Cédula/RIF: <strong className="text-white">{pm.documentId || "-"}</strong></p>
          <p className="text-zinc-400">Cuenta: <strong className="text-white break-all">{pm.accountNumber || "-"}</strong></p>
        </div>
      )}
      {isUsd && (
        <div className="space-y-1.5">
          <p className="text-zinc-400">Plataforma: <strong className="text-white">{pm.platformName || "-"}</strong></p>
          <p className="text-zinc-400">Correo/ID: <strong className="text-white break-all">{pm.accountIdentifier || "-"}</strong></p>
          {pm.accountHolder && <p className="text-zinc-400">Titular: <strong className="text-white">{pm.accountHolder}</strong></p>}
        </div>
      )}
    </div>
  );
}
